import * as yup from 'yup';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableInput } from '../../../Translations/Inputs';
import { emailRegex, getServerErrors, validateUrl } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import EventPicker from '../../EventPicker';
import UI from '../../../../common/UI';
import WebsiteInput from '../../../Common/WebsiteInput';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

const editEventDetailsSchema = yup.object().shape({
  title: yup.string().required(),
  ...dateRangeObject(true, false),
  location: yup.object().required().shape({
    id: yup.string().required(),
  }),
  website_url: yup.string().nullable().test((value) => !value || validateUrl(value)),
  email: yup.string().nullable().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
});

export interface EditEventDetailsFormProps {
  event: Event;
}

const getValues = (event: Event) => ({
  title: event.title,
  title_translations: translationsForAttribute(event.translations, 'title'),
  email: event.email,
  website_url: event.website_url,
  previous_edition_id: event.previous_edition?.id,
  location: {
    id: event.location.id,
    title: event.location.title,
    description: event.location.description,
    address: event.location.address,
    country: event.location.country,
    coordinates: event.location.coordinates,
  },
  start_date: event.start_date,
  end_date: event.start_date === event.end_date ? null : event.end_date,
  private: event.private,
});

const EditEventDetailsForm = ({ event }: EditEventDetailsFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const form = useForm({ values: getValues(event) });
  const locationBlurTimerRef = useRef<number>();

  useNavigationPrompt(form.changed());
  useEffect(() => () => window.clearTimeout(locationBlurTimerRef.current), []);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      details: {
        title: form.values.title,
        title_translations: form.values.title_translations,
        end_date: form.values.end_date || form.values.start_date,
        email: form.values.email,
        website_url: form.values.website_url,
        previous_edition_id: form.values.previous_edition_id,
        location: form.values.location,
        start_date: form.values.start_date,
        private: form.values.private,
      },
    },
    onSuccess: (updatedEvent) => form.reset(getValues(updatedEvent)),
  });

  const { valid, errors } = useValidation({
    schema: editEventDetailsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'details'),
  });

  const handleLocationBlur = () => {
    window.clearTimeout(locationBlurTimerRef.current);
    locationBlurTimerRef.current = window.setTimeout(() => form.setTouched('location', true), 300);
  };

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Legend>{t('details')}</UI.Legend>
        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
            <UI.InputLabel htmlFor="event_title">
              {t('event_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TranslatableInput
              form={form}
              errors={errors}
              name="title"
              id="event_title"
              sc={{ size: 'lg' }}
              defaultLocale={event.locale}
              placeholder={t('event_title_placeholder')}
            />
            <UI.ErrorMessages attribute={t('event_title')} errors={errors.title} />
          </UI.InputGroup>

          <DateRangeForm form={form} errors={errors} requireStart allowFullDay />

          <UI.InputGroup sc={{ valid: !errors['location.id'], touched: form.touched.location }}>
            <UI.InputLabel htmlFor="location">
              {t('location')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.GoogleLocationInput
              id="location"
              value={form.values.location}
              onChange={(location) => form.set('location', location)}
              onBlur={handleLocationBlur}
              placeholder={t('location_placeholder')}
            />
            <UI.ErrorMessages attribute={t('location')} errors={errors['location.id']} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.website_url, touched: form.touched.website_url }}>
            <UI.InputLabel htmlFor="website">{t('website')}</UI.InputLabel>
            <WebsiteInput form={form} formKey="website_url" id="website" />
            <UI.ErrorMessages attribute={t('website')} errors={errors.website_url} />
          </UI.InputGroup>

          <UI.InputGroup style={{ zIndex: 20 }}>
            <UI.InputLabel>{t('previous_edition')}</UI.InputLabel>
            <EventPicker
              eventId={form.values.previous_edition_id}
              onChange={(previousEdition) => form.set('previous_edition_id', previousEdition?.id || null)}
              nullableLabel={t('none')}
              startDateBefore={event.start_date}
            />
          </UI.InputGroup>

          <UI.HR />
          <UI.Legend>{t('email')}</UI.Legend>
          <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
            <UI.InputLabel htmlFor="email_address">{t('email_address')}</UI.InputLabel>
            <UI.InputDescription>{t('event_email_description')}</UI.InputDescription>
            <UI.DebouncedInput
              {...form.text('email')}
              placeholder={project.organisation_email}
              id="email_address"
            />
            <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
          </UI.InputGroup>

          <UI.HR />
          <UI.Legend>{t('visibility')}</UI.Legend>
          <UI.InputGroup sc={{ valid: !errors.private, touched: form.touched.private }}>
            <UI.InputLabel htmlFor="private">{t('event_visibility')}</UI.InputLabel>
            <UI.InputDescription>{t('private_event_description')}</UI.InputDescription>
            <UI.Checkbox {...form.checkbox('private')} id="private">
              {t('private_event')}
            </UI.Checkbox>
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />
        <UI.FormButtons form={form} submitLabel={t('save_event')} loading={loading} valid={valid} />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventDetailsForm;
